import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  MovementTypes,
  PaymentMethods,
} from '../../../../../utils/enums/userTypes';

const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const formatDateForDisplay = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toISOString().slice(0, 10);
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return String(value);
  }

  return `${new Intl.NumberFormat('hu-HU', {
    maximumFractionDigits: 0,
  }).format(number)} Ft`;
};

const getMovementTypeLabel = (value) => {
  const item = MovementTypes.find(
    (movementType) => String(movementType.key) === String(value),
  );

  return item?.name || value || '';
};

const getPaymentMethodLabel = (value) => {
  const item = PaymentMethods.find(
    (paymentMethod) => String(paymentMethod.key) === String(value),
  );

  return item?.name || value || '';
};

const getAmountColor = (value) => {
  const number = Number(value || 0);

  return number < 0 ? 'warning.main' : 'success.main';
};

const sumRows = (rows, field) => {
  return rows.reduce((sum, row) => sum + Number(row[field] || 0), 0);
};

const maxDate = (rows, field) => {
  const dates = rows
    .map((row) => row[field])
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter((value) => !Number.isNaN(value));

  if (dates.length === 0) return '';

  return formatDateForDisplay(new Date(Math.max(...dates)));
};

const downloadCsv = (rows, columns, filename) => {
  const header = columns.map((column) => column.label).join(';');

  const body = rows
    .map((row) =>
      columns
        .map((column) => {
          const rawValue = column.getValue(row);
          const value =
            rawValue === null || rawValue === undefined ? '' : String(rawValue);

          return `"${value.replaceAll('"', '""')}"`;
        })
        .join(';'),
    )
    .join('\n');

  const csv = `${header}\n${body}`;
  const blob = new Blob([`\uFEFF${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

const BalanceReportBase = ({
  title,
  description,
  endpoint,
  buildParams,
  groupField,
  groupLabel,
  secondGroupField,
  secondGroupLabel,
  activeField,
  amountField = 'dij',
  invoicedAmountField,
  descriptionField,
  showPaymentMethod = true,
  showInvoiceNumber = false,
  paymentButtonText,
}) => {
  const [rows, setRows] = useState([]);
  const [onlyActiveUser, setOnlyActiveUser] = useState(true);

  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams(buildParams(onlyActiveUser ? 1 : 0));

      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error('Nem sikerült betölteni az egyenleg riportot.');
      }

      const data = await response.json();

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyActiveUser]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    const sortedRows = [...rows].sort((a, b) => {
      const groupA = getNestedValue(a, groupField) || '';
      const groupB = getNestedValue(b, groupField) || '';

      const groupCompare = String(groupA).localeCompare(String(groupB), 'hu');

      if (groupCompare !== 0) {
        return groupCompare;
      }

      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });

    if (!normalizedSearch) {
      return sortedRows;
    }

    return sortedRows.filter((row) =>
      [
        getNestedValue(row, groupField),
        getNestedValue(row, secondGroupField),
        row.year,
        row.month,
        row.datum,
        formatDateForDisplay(row.datum),
        row.tipus,
        getMovementTypeLabel(row.tipus),
        row.fizmod,
        getPaymentMethodLabel(row.fizmod),
        row[amountField],
        invoicedAmountField ? row[invoicedAmountField] : '',
        row[descriptionField],
        row.invoice_nr,
        getNestedValue(row, 'creator.text'),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [
    rows,
    searchText,
    groupField,
    secondGroupField,
    amountField,
    invoicedAmountField,
    descriptionField,
  ]);

  const groupedRows = useMemo(() => {
    return filteredRows.reduce((groups, row) => {
      const mainGroup =
        getNestedValue(row, groupField) || `Nincs ${groupLabel}`;
      const secondGroup =
        getNestedValue(row, secondGroupField) || `Nincs ${secondGroupLabel}`;

      if (!groups[mainGroup]) {
        groups[mainGroup] = {};
      }

      if (!groups[mainGroup][secondGroup]) {
        groups[mainGroup][secondGroup] = [];
      }

      groups[mainGroup][secondGroup].push(row);

      return groups;
    }, {});
  }, [
    filteredRows,
    groupField,
    secondGroupField,
    groupLabel,
    secondGroupLabel,
  ]);
  const sortedGroupEntries = useMemo(() => {
    return Object.entries(groupedRows).sort(
      ([, secondGroupsA], [, secondGroupsB]) => {
        const rowsA = Object.values(secondGroupsA).flat();
        const rowsB = Object.values(secondGroupsB).flat();

        const sumA = sumRows(rowsA, amountField);
        const sumB = sumRows(rowsB, amountField);

        return sumA - sumB;
      },
    );
  }, [groupedRows, amountField]);
  const totalAmount = useMemo(() => {
    return sumRows(filteredRows, amountField);
  }, [filteredRows, amountField]);

  const totalInvoicedAmount = useMemo(() => {
    if (!invoicedAmountField) return 0;

    return sumRows(filteredRows, invoicedAmountField);
  }, [filteredRows, invoicedAmountField]);

  const getGroupKey = (...parts) => parts.join('__');

  const isGroupExpanded = (groupKey) => {
    return expandedGroups[groupKey] ?? false;
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((previous) => ({
      ...previous,
      [groupKey]: !(previous[groupKey] ?? false),
    }));
  };

  const handlePaymentClick = () => {
    window.alert('HAMAROSAN');
  };

  const exportRows = () => {
    const columns = [
      {
        label: groupLabel,
        getValue: (row) => getNestedValue(row, groupField),
      },
      {
        label: secondGroupLabel,
        getValue: (row) => getNestedValue(row, secondGroupField),
      },
      {
        label: 'Év',
        getValue: (row) => row.year,
      },
      {
        label: 'Hónap',
        getValue: (row) => row.month,
      },
      {
        label: 'Dátum',
        getValue: (row) => formatDateForDisplay(row.datum),
      },
      {
        label: 'Mozgás típusa',
        getValue: (row) => getMovementTypeLabel(row.tipus),
      },
      {
        label: 'Fizetési mód',
        getValue: (row) => getPaymentMethodLabel(row.fizmod),
      },
      {
        label: 'Összeg',
        getValue: (row) => row[amountField],
      },
      {
        label: 'Megjegyzés',
        getValue: (row) => row[descriptionField],
      },
      {
        label: 'Rögzítette',
        getValue: (row) => getNestedValue(row, 'creator.text'),
      },
    ];

    if (invoicedAmountField) {
      columns.splice(8, 0, {
        label: 'Ebből számlázva',
        getValue: (row) => row[invoicedAmountField],
      });
    }

    if (showInvoiceNumber) {
      columns.push({
        label: 'Számla száma',
        getValue: (row) => row.invoice_nr,
      });
    }

    downloadCsv(filteredRows, columns, `${title}.csv`);
  };

  const renderDataRow = (row, key) => {
    return (
      <TableRow key={key} hover>
        <TableCell>{row.year}</TableCell>
        <TableCell>{row.month}</TableCell>
        <TableCell>{formatDateForDisplay(row.datum)}</TableCell>
        <TableCell>{getMovementTypeLabel(row.tipus)}</TableCell>

        {showPaymentMethod && (
          <TableCell>{getPaymentMethodLabel(row.fizmod)}</TableCell>
        )}

        <TableCell align='right'>
          <Typography
            component='span'
            sx={{
              color: getAmountColor(row[amountField]),
              fontWeight: 700,
            }}
          >
            {formatMoney(row[amountField])}
          </Typography>
        </TableCell>

        {invoicedAmountField && (
          <TableCell align='right'>
            <Typography
              component='span'
              sx={{
                color: getAmountColor(row[invoicedAmountField]),
                fontWeight: 700,
              }}
            >
              {formatMoney(row[invoicedAmountField])}
            </Typography>
          </TableCell>
        )}

        <TableCell>{row[descriptionField]}</TableCell>

        {showInvoiceNumber && <TableCell>{row.invoice_nr}</TableCell>}

        <TableCell>{getNestedValue(row, 'creator.text')}</TableCell>
      </TableRow>
    );
  };

  const renderSecondGroup = (mainGroup, secondGroup, groupRows) => {
    const groupKey = getGroupKey(mainGroup, secondGroup);
    const expanded = isGroupExpanded(groupKey);
    const groupSum = sumRows(groupRows, amountField);
    const groupInvoicedSum = invoicedAmountField
      ? sumRows(groupRows, invoicedAmountField)
      : null;

    return (
      <Fragment key={groupKey}>
        <TableRow
          hover
          sx={{
            cursor: 'pointer',
            '& td': {
              bgcolor: 'action.hover',
              fontWeight: 700,
            },
          }}
          onClick={() => toggleGroup(groupKey)}
        >
          <TableCell
            colSpan={
              8 +
              (showPaymentMethod ? 1 : 0) +
              (invoicedAmountField ? 1 : 0) +
              (showInvoiceNumber ? 1 : 0)
            }
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Typography variant='body2' sx={{ fontWeight: 700 }}>
                {expanded ? '▾' : '▸'} {secondGroupLabel}: {secondGroup}
              </Typography>

              <Chip
                size='small'
                variant='outlined'
                label={`${groupRows.length} tranzakció`}
              />

              <Chip
                size='small'
                color={groupSum < 0 ? 'warning' : 'success'}
                variant='outlined'
                label={`Összeg: ${formatMoney(groupSum)}`}
              />

              {invoicedAmountField && (
                <Chip
                  size='small'
                  variant='outlined'
                  label={`Számlázva: ${formatMoney(groupInvoicedSum)}`}
                />
              )}

              <Chip
                size='small'
                variant='outlined'
                label={`Utolsó dátum: ${maxDate(groupRows, 'datum') || '-'}`}
              />
            </Stack>
          </TableCell>
        </TableRow>

        {expanded &&
          groupRows.map((row, index) =>
            renderDataRow(
              row,
              `${mainGroup}_${secondGroup}_${row.datum}_${index}`,
            ),
          )}
      </Fragment>
    );
  };

  const renderMainGroup = ([mainGroup, secondGroups]) => {
    const groupKey = getGroupKey(mainGroup);
    const expanded = isGroupExpanded(groupKey);
    const groupRows = Object.values(secondGroups).flat();

    const groupSum = sumRows(groupRows, amountField);
    const groupInvoicedSum = invoicedAmountField
      ? sumRows(groupRows, invoicedAmountField)
      : null;

    return (
      <Fragment key={groupKey}>
        <TableRow
          hover
          sx={{
            cursor: 'pointer',
            '& td': {
              bgcolor: 'action.selected',
              fontWeight: 700,
            },
          }}
          onClick={() => toggleGroup(groupKey)}
        >
          <TableCell
            colSpan={
              8 +
              (showPaymentMethod ? 1 : 0) +
              (invoicedAmountField ? 1 : 0) +
              (showInvoiceNumber ? 1 : 0)
            }
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Typography variant='body2' sx={{ fontWeight: 700 }}>
                {expanded ? '▾' : '▸'} {groupLabel}: {mainGroup}
              </Typography>

              <Button
                size='small'
                variant='outlined'
                onClick={handlePaymentClick}
              >
                {paymentButtonText}
              </Button>

              <Chip
                size='small'
                variant='outlined'
                label={`${groupRows.length} tranzakció`}
              />

              <Chip
                size='small'
                color={groupSum < 0 ? 'warning' : 'success'}
                variant='outlined'
                label={`Egyenleg: ${formatMoney(groupSum)}`}
              />

              {invoicedAmountField && (
                <Chip
                  size='small'
                  variant='outlined'
                  label={`Számlázva: ${formatMoney(groupInvoicedSum)}`}
                />
              )}

              <Chip
                size='small'
                variant='outlined'
                label={`Utolsó dátum: ${maxDate(groupRows, 'datum') || '-'}`}
              />
            </Stack>
          </TableCell>
        </TableRow>

        {expanded &&
          Object.entries(secondGroups).map(([secondGroup, groupRows]) =>
            renderSecondGroup(mainGroup, secondGroup, groupRows),
          )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'stretch', lg: 'center' }}
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {title}
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    {description}
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={onlyActiveUser}
                        onChange={(event) =>
                          setOnlyActiveUser(event.target.checked)
                        }
                      />
                    }
                    label='Aktív felhasználók'
                  />

                  <TextField
                    label='Keresés'
                    size='small'
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    sx={{ minWidth: 260 }}
                  />

                  <Button
                    variant='outlined'
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    disabled={loading}
                  >
                    Frissítés
                  </Button>

                  <Button
                    variant='outlined'
                    startIcon={<FileDownloadIcon />}
                    onClick={exportRows}
                    disabled={loading || filteredRows.length === 0}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <Chip
                  label={`${filteredRows.length} tranzakció`}
                  variant='outlined'
                />

                <Chip
                  label={`Összesen: ${formatMoney(totalAmount)}`}
                  color={totalAmount < 0 ? 'warning' : 'success'}
                  variant='outlined'
                />

                {invoicedAmountField && (
                  <Chip
                    label={`Ebből számlázva: ${formatMoney(
                      totalInvoicedAmount,
                    )}`}
                    variant='outlined'
                  />
                )}
              </Stack>

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  variant='outlined'
                  sx={{ overflow: 'auto' }}
                >
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Év</TableCell>
                        <TableCell>Hónap</TableCell>
                        <TableCell>Dátum</TableCell>
                        <TableCell>Mozgás típusa</TableCell>

                        {showPaymentMethod && (
                          <TableCell>Fizetési mód</TableCell>
                        )}

                        <TableCell align='right'>Összeg</TableCell>

                        {invoicedAmountField && (
                          <TableCell align='right'>Ebből számlázva</TableCell>
                        )}

                        <TableCell>Megjegyzés</TableCell>

                        {showInvoiceNumber && (
                          <TableCell>Számla száma</TableCell>
                        )}

                        <TableCell>Rögzítette</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {sortedGroupEntries.map(renderMainGroup)}
                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={
                              8 +
                              (showPaymentMethod ? 1 : 0) +
                              (invoicedAmountField ? 1 : 0) +
                              (showInvoiceNumber ? 1 : 0)
                            }
                            align='center'
                          >
                            Nincs megjeleníthető tranzakció.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

export default BalanceReportBase;
